import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  SalesDashboardState,
  DailySalesData,
  ComparisonData,
  TargetProgressData,
  MonthlyTarget,
  DateRange,
  DatePreset,
  ComparisonType,
  BreakdownDimension,
  SalesDashboardFilters,
  FetchSalesDashboardRequest,
  FetchComparisonRequest,
  CreateTargetPayload,
  UpdateTargetPayload,
} from '../domain/types'
import { DEFAULT_DATE_RANGE, DEFAULT_COMPARISON_TYPE } from '../domain/constants'

const initialState: SalesDashboardState = {
  dailyData: null,
  comparisonData: null,
  targetProgress: null,
  targets: null,
  dateRange: DEFAULT_DATE_RANGE, // last_week — matches DEFAULT_COMPARISON_TYPE 'wow'
  comparisonType: DEFAULT_COMPARISON_TYPE,
  filters: {},
  groupBy: null,
  loading: {
    daily: false,
    comparison: false,
    targetProgress: false,
    targets: false,
    savingTarget: false,
  },
  error: { message: null },
  lastRefreshed: null,
}

export const salesDashboardSlice = createSlice({
  name: 'salesDashboard',
  initialState,
  reducers: {
    // ===== Fetch Daily Sales =====
    fetchDailySales: (state, _action: PayloadAction<FetchSalesDashboardRequest | undefined>) => {
      state.loading.daily = true
      state.error.message = null
    },
    fetchDailySalesSuccess: (state, action: PayloadAction<DailySalesData>) => {
      state.dailyData = action.payload
      state.loading.daily = false
      state.lastRefreshed = new Date().toISOString()
    },
    fetchDailySalesFailure: (state, action: PayloadAction<string>) => {
      state.loading.daily = false
      state.error.message = action.payload
    },

    // ===== Fetch Comparison =====
    fetchComparison: (state, _action: PayloadAction<FetchComparisonRequest | undefined>) => {
      state.loading.comparison = true
      state.error.message = null
    },
    fetchComparisonSuccess: (state, action: PayloadAction<ComparisonData>) => {
      state.comparisonData = action.payload
      state.loading.comparison = false
    },
    fetchComparisonFailure: (state, action: PayloadAction<string>) => {
      state.loading.comparison = false
      state.error.message = action.payload
    },

    // ===== Fetch Target Progress =====
    fetchTargetProgress: (state, _action: PayloadAction<{ months: string[]; factoryIds?: string[] } | undefined>) => {
      state.loading.targetProgress = true
    },
    fetchTargetProgressSuccess: (state, action: PayloadAction<TargetProgressData[]>) => {
      state.targetProgress = action.payload
      state.loading.targetProgress = false
    },
    fetchTargetProgressFailure: (state, action: PayloadAction<string>) => {
      state.loading.targetProgress = false
      state.error.message = action.payload
    },

    // ===== Fetch Targets (CRUD list) =====
    fetchTargets: (state, _action: PayloadAction<string | undefined>) => {
      state.loading.targets = true
    },
    fetchTargetsSuccess: (state, action: PayloadAction<{ items: MonthlyTarget[]; total: number }>) => {
      state.targets = action.payload
      state.loading.targets = false
    },
    fetchTargetsFailure: (state, action: PayloadAction<string>) => {
      state.loading.targets = false
      state.error.message = action.payload
    },

    // ===== Create Target =====
    createTarget: (state, _action: PayloadAction<CreateTargetPayload>) => {
      state.loading.savingTarget = true
      state.error.message = null
    },
    createTargetSuccess: (state) => {
      state.loading.savingTarget = false
    },
    createTargetFailure: (state, action: PayloadAction<string>) => {
      state.loading.savingTarget = false
      state.error.message = action.payload
    },

    // ===== Update Target =====
    updateTarget: (state, _action: PayloadAction<{ documentId: string; data: UpdateTargetPayload }>) => {
      state.loading.savingTarget = true
      state.error.message = null
    },
    updateTargetSuccess: (state) => {
      state.loading.savingTarget = false
    },
    updateTargetFailure: (state, action: PayloadAction<string>) => {
      state.loading.savingTarget = false
      state.error.message = action.payload
    },

    // ===== Delete Target =====
    deleteTarget: (state, _action: PayloadAction<string>) => {
      state.loading.savingTarget = true
    },
    deleteTargetSuccess: (state) => {
      state.loading.savingTarget = false
    },
    deleteTargetFailure: (state, action: PayloadAction<string>) => {
      state.loading.savingTarget = false
      state.error.message = action.payload
    },

    // ===== Filter & Control Actions =====
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload
    },
    setDatePreset: (state, action: PayloadAction<DatePreset>) => {
      state.dateRange = { preset: action.payload, from: null, to: null }
    },
    setCustomDateRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
      state.dateRange = { preset: 'custom', from: action.payload.from, to: action.payload.to }
    },
    setComparisonType: (state, action: PayloadAction<ComparisonType>) => {
      state.comparisonType = action.payload
      // Auto-set the date range to the last completed period (excluding Fridays)
      if (action.payload === 'dod') {
        state.dateRange = { preset: 'yesterday', from: null, to: null }
      } else if (action.payload === 'wow') {
        state.dateRange = { preset: 'last_week', from: null, to: null }
      } else if (action.payload === 'mom') {
        state.dateRange = { preset: 'last_month', from: null, to: null }
      }
    },
    setFilters: (state, action: PayloadAction<SalesDashboardFilters>) => {
      state.filters = action.payload
    },
    setGroupBy: (state, action: PayloadAction<BreakdownDimension | null>) => {
      state.groupBy = action.payload
    },

    // ===== Init Dashboard (triggers fetchAllDashboardData saga) =====
    initDashboard: (state) => {
      state.loading.daily = true
      state.loading.comparison = true
      state.loading.targetProgress = true
      state.error.message = null
    },

    // ===== Reset =====
    resetDashboard: () => initialState,
  },
})

export const salesDashboardActions = salesDashboardSlice.actions
export const salesDashboardReducer = salesDashboardSlice.reducer
